import { TILIA_STATUS_PENDING } from '@config/monetization.js'
import { areBitsSet, isObj, clone } from '@helpers/utils.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import useStorage from '@composables/useStorage.js'
import { GAME_ID } from '@config/game.js'
import { computed } from 'vue'
import {
  collectionFollowingStore,
  partnerProgramStore,
  userFollowingStore,
  subscriptionStore,
  blockedUsersStore,
  mutedUsersStore,
  purchasesStore,
  myTeamsStore,
  ratingStore,
  karmaStore,
} from '@stores'
import {
  LS_MONACCESSTOKEN,
  LS_ACCESSTOKEN,
  LS_USER,
} from '@config/localstorage.js'

const DEFAULT_LANG = 'en'
const accessTokenState = useStorage(LS_ACCESSTOKEN, null, false)
const monAccessTokenState = useStorage(LS_MONACCESSTOKEN, null, false)
const userState = useStorage(LS_USER, null, false)

export default function useAuth() {
  const { subscriptions, clearSubscriptions } = subscriptionStore()
  const { clearMyFollowedCollections } = collectionFollowingStore()
  const { clearPartnerPrograms } = partnerProgramStore()
  const { clearMyFollowedUsers } = userFollowingStore()
  const { clearBlockedUsers } = blockedUsersStore()
  const { clearMutedUsers } = mutedUsersStore()
  const { clearMyTeamsStore } = myTeamsStore()
  const { clearPurchases } = purchasesStore()
  const { clearRatings } = ratingStore()
  const { clearKarma } = karmaStore()

  const accessTokens = computed(() => {
    try {
      const json = JSON.parse(accessTokenState.value)
      if (isObj(json)) return json
    } catch (error) {
      console.error(error)
    }
    return {}
  })
  const monAccessToken = computed(() => JSON.parse(monAccessTokenState.value))
  const user = computed(() => JSON.parse(userState.value))

  const isLoggedIn = computed(() =>
    user.value?.id && accessTokens.value[GAME_ID] ? true : false
  )

  const userOnboardingStatus = computed(() => user.value?.monetization_status)

  const isMonRegistered = computed(() =>
    areBitsSet(userOnboardingStatus.value, TILIA_STATUS_PENDING)
  )

  function setUser(user) {
    if (!user) {
      throw new Error()
    }
    userState.value = JSON.stringify(user)
    sdkInterface.setUserId(user.id)
  }

  function setUserMonetizationStatus(monetization_status) {
    setUser({ ...user.value, monetization_status })
  }

  function setToken(accessToken) {
    if (!accessToken) {
      throw new Error()
    }
    accessTokenState.value = JSON.stringify({
      ...accessTokens.value,
      [GAME_ID]: accessToken,
    })
  }

  function setMonToken(_monAccessToken, scope) {
    if (!_monAccessToken) {
      throw new Error()
    }
    monAccessTokenState.value = JSON.stringify({
      ...monAccessToken.value,
      [scope]: _monAccessToken,
    })
  }

  function clearMonToken() {
    monAccessTokenState.value = null
  }

  function clearUser() {
    clearMyFollowedCollections()
    clearPartnerPrograms()
    clearMyFollowedUsers()
    clearSubscriptions()
    clearMyTeamsStore()
    clearBlockedUsers()
    clearMutedUsers()
    clearPurchases()
    clearRatings()
    clearKarma()
    userState.value = JSON.stringify({})
    const tokens = clone(accessTokens.value)
    delete tokens[GAME_ID]
    accessTokenState.value = JSON.stringify(tokens)
  }

  function getUserId() {
    return user.value?.id
  }

  function getUserLanguage() {
    return user.value?.language || DEFAULT_LANG
  }

  return {
    setUserMonetizationStatus,
    userOnboardingStatus,
    getUserLanguage,
    isMonRegistered,
    monAccessToken,
    subscriptions,
    clearMonToken,
    accessTokens,
    setMonToken,
    isLoggedIn,
    getUserId,
    clearUser,
    setToken,
    setUser,
    user,
  }
}
